import { useCallback, useEffect, useRef, useState } from "react";
import {
  createRealtimeSessionApi,
  getChatDescriptionByChatIdApi,
} from "../Redux/api/realtime";

const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

function createAnalyserFromStream(stream) {
  if (!stream) return { ctx: null, analyser: null };
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);
  return { ctx, analyser };
}

/**
 * OpenAI Realtime WebRTC session hook.
 *
 * @param {object} options
 * @param {object} options.sessionPayload - body for POST /api/realtime/session
 * @param {string|number|null} options.chatId - existing chat for history replay
 * @param {function} options.onTurn - (role, text) => void
 * @param {function} options.onStageChange - ({ stage, stage_name }) => void
 * @param {function} options.onOpenAnatomy - ({ view }) => void
 * @param {function} options.onEndSession - ({ closing_note }) => void
 * @param {function} options.onError - (message) => void
 * @param {number} [options.initialStage=1]
 */
export default function useRealtimeSession({
  sessionPayload,
  chatId = null,
  onTurn,
  onStageChange,
  onOpenAnatomy,
  onEndSession,
  onError,
  initialStage = 1,
} = {}) {
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | reconnecting | error | ended
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [assistantCaption, setAssistantCaption] = useState("");
  const [error, setError] = useState(null);
  const [stages, setStages] = useState([]);
  const [physicianName, setPhysicianName] = useState("");

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const audioElRef = useRef(null);
  const micAnalyserRef = useRef(null);
  const remoteAnalyserRef = useRef(null);
  const micCtxRef = useRef(null);
  const remoteCtxRef = useRef(null);
  const assistantBufRef = useRef("");
  const toolArgsBufRef = useRef({});
  const handledCallsRef = useRef(new Set());
  const pendingEndSessionRef = useRef(null);
  const intentionalDisconnectRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const callbacksRef = useRef({
    onTurn,
    onStageChange,
    onOpenAnatomy,
    onEndSession,
    onError,
  });
  const sessionPayloadRef = useRef(sessionPayload);
  const chatIdRef = useRef(chatId);
  const initialStageRef = useRef(initialStage);

  useEffect(() => {
    callbacksRef.current = {
      onTurn,
      onStageChange,
      onOpenAnatomy,
      onEndSession,
      onError,
    };
  }, [onTurn, onStageChange, onOpenAnatomy, onEndSession, onError]);

  useEffect(() => {
    sessionPayloadRef.current = sessionPayload;
  }, [sessionPayload]);

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    initialStageRef.current = initialStage;
  }, [initialStage]);

  const reportError = useCallback((message) => {
    setError(message);
    setStatus("error");
    callbacksRef.current.onError?.(message);
  }, []);

  const sendEvent = useCallback((event) => {
    const dc = dcRef.current;
    if (dc && dc.readyState === "open") {
      dc.send(JSON.stringify(event));
    }
  }, []);

  const cleanupMedia = useCallback(() => {
    try {
      dcRef.current?.close();
    } catch (_) {
      /* ignore */
    }
    dcRef.current = null;

    try {
      pcRef.current?.close();
    } catch (_) {
      /* ignore */
    }
    pcRef.current = null;

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    remoteStreamRef.current = null;

    if (audioElRef.current) {
      audioElRef.current.srcObject = null;
      audioElRef.current = null;
    }

    try {
      micCtxRef.current?.close();
    } catch (_) {
      /* ignore */
    }
    try {
      remoteCtxRef.current?.close();
    } catch (_) {
      /* ignore */
    }
    micCtxRef.current = null;
    remoteCtxRef.current = null;
    micAnalyserRef.current = null;
    remoteAnalyserRef.current = null;
  }, []);

  const handleToolCall = useCallback(
    async (name, args, callId) => {
      if (callId && handledCallsRef.current.has(callId)) {
        return;
      }
      if (callId) {
        handledCallsRef.current.add(callId);
      }

      let result = { ok: true };
      try {
        if (name === "set_conversation_stage") {
          const stage = Number(args?.stage);
          if (Number.isFinite(stage) && stage >= 1 && stage <= 8) {
            callbacksRef.current.onStageChange?.({
              stage,
              stage_name: args?.stage_name || "",
            });
            result = { ok: true, stage };
          } else {
            result = { ok: false, error: "invalid stage" };
          }
        } else if (name === "open_anatomy_selector") {
          callbacksRef.current.onOpenAnatomy?.({
            view: args?.view || "full_body",
          });
          result = { ok: true };
        } else if (name === "end_session") {
          pendingEndSessionRef.current = {
            closing_note: args?.closing_note || "",
          };
          result = { ok: true };
        } else {
          result = { ok: false, error: `unknown tool ${name}` };
        }
      } catch (err) {
        result = { ok: false, error: String(err?.message || err) };
      }

      sendEvent({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: callId,
          output: JSON.stringify(result),
        },
      });
      if (name !== "end_session") {
        sendEvent({ type: "response.create" });
      }
    },
    [sendEvent]
  );

  const handleServerEvent = useCallback(
    (raw) => {
      let event;
      try {
        event = JSON.parse(raw);
      } catch (_) {
        return;
      }

      const type = event?.type;

      if (type === "error") {
        const message =
          event?.error?.message || event?.message || "Realtime session error";
        reportError(message);
        return;
      }

      if (type === "input_audio_buffer.speech_started") {
        setIsUserSpeaking(true);
        return;
      }
      if (type === "input_audio_buffer.speech_stopped") {
        setIsUserSpeaking(false);
        return;
      }

      if (type === "response.created") {
        setIsAssistantSpeaking(true);
        assistantBufRef.current = "";
        setAssistantCaption("");
        return;
      }
      if (type === "response.done" || type === "response.cancelled") {
        setIsAssistantSpeaking(false);
        const finalText = assistantBufRef.current.trim();
        if (finalText) {
          callbacksRef.current.onTurn?.("assistant", finalText);
        }
        assistantBufRef.current = "";
        if (type === "response.done" && pendingEndSessionRef.current) {
          const terminalPayload = pendingEndSessionRef.current;
          pendingEndSessionRef.current = null;
          callbacksRef.current.onEndSession?.(terminalPayload);
        }
        return;
      }

      if (
        type === "response.output_audio_transcript.delta" ||
        type === "response.audio_transcript.delta"
      ) {
        const delta = event?.delta || "";
        assistantBufRef.current += delta;
        setAssistantCaption(assistantBufRef.current);
        return;
      }

      if (
        type === "response.output_audio_transcript.done" ||
        type === "response.audio_transcript.done"
      ) {
        const text =
          event?.transcript || assistantBufRef.current || "";
        assistantBufRef.current = text;
        setAssistantCaption(text);
        return;
      }

      if (type === "conversation.item.input_audio_transcription.completed") {
        const text = (event?.transcript || "").trim();
        if (text) {
          callbacksRef.current.onTurn?.("user", text);
        }
        return;
      }

      // Accumulate function-call argument deltas (GA event names)
      if (
        type === "response.function_call_arguments.delta" ||
        type === "response.output_item.added"
      ) {
        if (type === "response.output_item.added") {
          const item = event?.item;
          if (item?.type === "function_call" && item?.call_id) {
            toolArgsBufRef.current[item.call_id] = {
              name: item.name,
              arguments: item.arguments || "",
            };
          }
          return;
        }
        const callId = event?.call_id;
        if (callId) {
          if (!toolArgsBufRef.current[callId]) {
            toolArgsBufRef.current[callId] = {
              name: event?.name,
              arguments: "",
            };
          }
          toolArgsBufRef.current[callId].arguments += event?.delta || "";
        }
        return;
      }

      if (type === "response.function_call_arguments.done") {
        const callId = event?.call_id;
        const name =
          event?.name || toolArgsBufRef.current[callId]?.name || "";
        const argsText =
          event?.arguments ||
          toolArgsBufRef.current[callId]?.arguments ||
          "{}";
        let args = {};
        try {
          args = JSON.parse(argsText || "{}");
        } catch (_) {
          args = {};
        }
        delete toolArgsBufRef.current[callId];
        handleToolCall(name, args, callId);
        return;
      }

      // Some GA builds emit completed function calls as response.output_item.done
      if (type === "response.output_item.done") {
        const item = event?.item;
        if (item?.type === "function_call" && item?.call_id) {
          let args = {};
          try {
            args = JSON.parse(item.arguments || "{}");
          } catch (_) {
            args = {};
          }
          handleToolCall(item.name, args, item.call_id);
        }
      }
    },
    [handleToolCall, reportError]
  );

  const replayHistory = useCallback(
    async (existingChatId) => {
      if (!existingChatId) return;
      try {
        const res = await getChatDescriptionByChatIdApi(existingChatId);
        const history = res?.data?.data;
        if (!Array.isArray(history) || history.length === 0) return;

        // History from BE is typically alternating strings [assistant, user, assistant, ...]
        // or objects. Normalize to role/text pairs.
        history.forEach((entry, index) => {
          let text = "";
          let role = index % 2 === 0 ? "assistant" : "user";

          if (typeof entry === "string") {
            text = entry;
          } else if (entry && typeof entry === "object") {
            text =
              entry.description ||
              entry.message ||
              entry.content ||
              entry.text ||
              "";
            if (entry.role === "user" || entry.fromUser) {
              role = "user";
            } else if (
              entry.role === "assistant" ||
              entry.fromUser === "Robot Doctor" ||
              entry.isBot
            ) {
              role = "assistant";
            }
          }

          text = String(text || "").trim();
          if (!text) return;

          sendEvent({
            type: "conversation.item.create",
            item: {
              type: "message",
              role,
              content: [
                {
                  type: role === "user" ? "input_text" : "output_text",
                  text,
                },
              ],
            },
          });
        });
      } catch (err) {
        console.warn("Failed to replay chat history:", err);
      }
    },
    [sendEvent]
  );

  const startSession = useCallback(
    async ({ isReconnect = false } = {}) => {
      intentionalDisconnectRef.current = false;
      setError(null);
      setStatus(isReconnect ? "reconnecting" : "connecting");

      try {
        const payload = sessionPayloadRef.current || {};
        const tokenRes = await createRealtimeSessionApi(payload);
        const data = tokenRes?.data?.data || tokenRes?.data;
        if (!data?.value) {
          throw new Error(
            tokenRes?.data?.message || "Failed to obtain ephemeral key"
          );
        }

        if (Array.isArray(data.stages)) {
          setStages(data.stages);
        }
        if (data.physician_name) {
          setPhysicianName(data.physician_name);
        }

        const ephemeralKey = data.value;

        cleanupMedia();
        handledCallsRef.current = new Set();
        pendingEndSessionRef.current = null;

        const pc = new RTCPeerConnection();
        pcRef.current = pc;

        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioElRef.current = audioEl;

        pc.ontrack = (e) => {
          const stream = e.streams?.[0];
          if (!stream) return;
          remoteStreamRef.current = stream;
          audioEl.srcObject = stream;
          const { ctx, analyser } = createAnalyserFromStream(stream);
          remoteCtxRef.current = ctx;
          remoteAnalyserRef.current = analyser;
        };

        pc.onconnectionstatechange = () => {
          const state = pc.connectionState;
          if (state === "failed" || state === "disconnected") {
            if (
              !intentionalDisconnectRef.current &&
              reconnectAttemptRef.current < 2
            ) {
              reconnectAttemptRef.current += 1;
              setStatus("reconnecting");
              setTimeout(() => {
                startSession({ isReconnect: true });
              }, 1200);
            } else if (!intentionalDisconnectRef.current) {
              reportError("Realtime connection lost");
            }
          } else if (state === "connected") {
            reconnectAttemptRef.current = 0;
            setStatus("connected");
          }
        };

        const ms = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
          },
        });
        localStreamRef.current = ms;
        ms.getTracks().forEach((track) => pc.addTrack(track, ms));
        const { ctx: micCtx, analyser: micAnalyser } =
          createAnalyserFromStream(ms);
        micCtxRef.current = micCtx;
        micAnalyserRef.current = micAnalyser;

        const dc = pc.createDataChannel("oai-events");
        dcRef.current = dc;
        dc.addEventListener("message", (e) => handleServerEvent(e.data));
        dc.addEventListener("open", async () => {
          await replayHistory(chatIdRef.current);
          // Kick off the interview (or continue after history replay)
          sendEvent({ type: "response.create" });
          setStatus("connected");
        });

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const sdpResponse = await fetch(REALTIME_CALLS_URL, {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
          },
        });

        if (!sdpResponse.ok) {
          const errText = await sdpResponse.text();
          throw new Error(
            `Realtime SDP exchange failed (${sdpResponse.status}): ${errText}`
          );
        }

        const answer = {
          type: "answer",
          sdp: await sdpResponse.text(),
        };
        await pc.setRemoteDescription(answer);
      } catch (err) {
        cleanupMedia();
        reportError(err?.message || "Failed to start realtime session");
      }
    },
    [
      cleanupMedia,
      handleServerEvent,
      replayHistory,
      reportError,
      sendEvent,
    ]
  );

  const connect = useCallback(() => startSession({ isReconnect: false }), [
    startSession,
  ]);

  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    cleanupMedia();
    setIsUserSpeaking(false);
    setIsAssistantSpeaking(false);
    setAssistantCaption("");
    setStatus("ended");
  }, [cleanupMedia]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !isMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
    setIsMuted(next);
  }, [isMuted]);

  const sendUserText = useCallback(
    (text) => {
      const trimmed = String(text || "").trim();
      if (!trimmed) return;
      sendEvent({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [{ type: "input_text", text: trimmed }],
        },
      });
      sendEvent({ type: "response.create" });
      callbacksRef.current.onTurn?.("user", trimmed);
    },
    [sendEvent]
  );

  // Teardown on unmount
  useEffect(() => {
    return () => {
      intentionalDisconnectRef.current = true;
      cleanupMedia();
    };
  }, [cleanupMedia]);

  return {
    status,
    error,
    isUserSpeaking,
    isAssistantSpeaking,
    isMuted,
    assistantCaption,
    stages,
    physicianName,
    micAnalyserRef,
    remoteAnalyserRef,
    connect,
    disconnect,
    toggleMute,
    sendUserText,
  };
}
